import React from 'react';
import {
  HiSparkles,
  HiUsers,
  HiOfficeBuilding,
  HiChat,
  HiBell,
  HiFolder,
} from 'react-icons/hi';
import type { NavigateFunction } from 'react-router-dom';
import BulletedListIcon from '../components/custom-icons/BulletedList';

/**
 * As seções do produto — **fonte única**, consumida pela barra lateral e pelos atalhos da tela
 * inicial.
 *
 * ## Por que centralizar
 *
 * A barra lateral declarava as sete seções em JSX escrito à mão, cada uma com o próprio ícone, a
 * própria rota e a própria regra de permissão. Duplicar isso na tela inicial criaria o pior tipo de
 * duplicação: a que **divergindo, não quebra**. Uma seção nova apareceria só num dos lugares, e um
 * atalho continuaria visível para quem perdeu acesso — sem erro, sem teste vermelho, sem sintoma
 * até alguém clicar.
 *
 * A regra de permissão é a parte mais sensível: um atalho que leva a uma tela que o usuário não pode
 * abrir não é um detalhe estético, é uma promessa falsa. Aqui ela é declarada uma vez.
 *
 * ## Ordem
 *
 * A ordem do array É a ordem exibida, nos dois lugares. Ela reproduz exatamente a que a barra
 * lateral já tinha, para a mudança não reorganizar uma interface que já está aprovada.
 */
export interface Secao {
  /** Identificador estável. Usado como chave de lista e pela marcação de item ativo. */
  chave: 'dashboard' | 'users' | 'organizations' | 'compliance' | 'rules' | 'folders' | 'results';
  /** Chave de i18n do rótulo curto (o mesmo texto da barra lateral). */
  rotulo: string;
  /** Chave de i18n da frase de apoio, usada só nos atalhos da tela inicial. */
  descricao: string;
  /** Ícone. Recebe o tamanho porque a barra usa 20px e os atalhos usam maior. */
  icone: (tamanho: number) => React.ReactNode;
  /**
   * Navegação. É função, e não string de rota, porque o Chat não é uma rota simples: ele precisa de
   * query string e de `state`, e reduzir tudo a um caminho perderia isso.
   */
  navegar: (navigate: NavigateFunction) => void;
  /** Quem pode ver. `undefined` significa "todos". */
  visivel?: (permissoes: Permissoes) => boolean;
}

export interface Permissoes {
  admin: boolean;
  manager: boolean;
}

export const SECOES: readonly Secao[] = [
  {
    chave: 'dashboard',
    rotulo: 'sidebar.dashboard',
    descricao: 'home.descricao.dashboard',
    icone: (t) => <HiSparkles size={t} />,
    navegar: (navigate) => navigate('/'),
  },
  {
    chave: 'users',
    rotulo: 'sidebar.users',
    descricao: 'home.descricao.users',
    icone: (t) => <HiUsers size={t} />,
    navegar: (navigate) => navigate('/users'),
    visivel: ({ admin, manager }) => admin || manager,
  },
  {
    chave: 'organizations',
    rotulo: 'sidebar.organizations',
    descricao: 'home.descricao.organizations',
    icone: (t) => <HiOfficeBuilding size={t} />,
    navegar: (navigate) => navigate('/organizations'),
    visivel: ({ admin }) => admin,
  },
  {
    chave: 'compliance',
    rotulo: 'sidebar.chat',
    descricao: 'home.descricao.chat',
    icone: (t) => <HiChat size={t} />,
    /*
      O Chat não é uma rota simples. O `state` é lido pela página de destino para abrir direto no
      chat em vez de na listagem — perder qualquer um destes campos muda o comportamento, então eles
      são reproduzidos aqui exatamente como a barra lateral os enviava.
    */
    navegar: (navigate) =>
      navigate('/compliance/pending?full=true', {
        state: {
          complianceType: 'menu',
          isNewSubmission: true,
          fromSidebar: true,
          directChat: true,
        },
      }),
  },
  {
    chave: 'rules',
    rotulo: 'sidebar.rules',
    descricao: 'home.descricao.rules',
    icone: (t) => <HiBell size={t} />,
    navegar: (navigate) => navigate('/rules'),
    visivel: ({ admin }) => admin,
  },
  {
    chave: 'folders',
    rotulo: 'sidebar.folders',
    descricao: 'home.descricao.folders',
    icone: (t) => <HiFolder size={t} />,
    navegar: (navigate) => navigate('/folders'),
  },
  {
    chave: 'results',
    rotulo: 'sidebar.results',
    descricao: 'home.descricao.results',
    icone: (t) => <BulletedListIcon size={t} />,
    navegar: (navigate) => navigate('/results'),
  },
] as const;

/** As seções que este usuário pode ver, na ordem de exibição. */
export const secoesVisiveis = (permissoes: Permissoes): Secao[] =>
  SECOES.filter((s) => !s.visivel || s.visivel(permissoes));
